import React, { memo, useContext, useMemo, useState } from 'react'
import { isMultilangPocEnabled } from '../../../constants/featureFlags'
import useGetDraftQuestionnaire from '../../../hooks/questionnaire/useGetDraftQuestionnaire'
import { useFieldwork } from '../../../hooks/useFieldwork'
import { applyTranslationOverlay } from '../../Translations/lib/previewOverlay'
import QuestionnaireContext, {
  closePreviewPanel
} from '../Questionnaire.context'
import { flattenEntries } from '../Questionnaire.utils'
import PreviewPanelControl from './PreviewPanel.control'

// The Translations editor always authors against English as the master /
// source language, regardless of the survey's country or locale. Preview
// mirrors that contract: English is the default ("master") option, and the
// languages the researcher can preview into come from the Audience page's
// language allocations — exactly the same source the Translations route uses.
const MASTER_LANGUAGE = 'en'

const PreviewPanel: React.FC = () => {
  const { draftQuestionnaire, refetchQuestionnaire } =
    useGetDraftQuestionnaire()
  const { fieldwork } = useFieldwork()
  const [previewLanguage, setPreviewLanguage] =
    useState<string>(MASTER_LANGUAGE)

  // English (master) first, then every language allocated on the Audience
  // page (fieldwork.languageAllocations). This is the same list the
  // Translations route offers, so the two stay in lockstep: changing the
  // country resets the allocations, which immediately changes what Preview
  // can switch to. Translations are stored per-language with no country
  // binding, so a no-longer-allocated language (e.g. Dutch after switching
  // Belgium → Canada) drops out of the dropdown while its saved text is
  // preserved; re-allocating that language brings it back. A still-allocated
  // language (e.g. French, shared by Belgium and Canada) keeps showing its
  // existing translation.
  const availableLanguages = useMemo(() => {
    if (!isMultilangPocEnabled()) return [MASTER_LANGUAGE]
    const allocated = (fieldwork?.languageAllocations ?? []).map(
      (a) => a.language
    )
    return [
      MASTER_LANGUAGE,
      ...allocated.filter((code) => code !== MASTER_LANGUAGE)
    ]
  }, [fieldwork?.languageAllocations])

  const flatDraftQuestionnaireEntries = useMemo(() => {
    const flat = flattenEntries(draftQuestionnaire?.entries ?? [])
    if (!isMultilangPocEnabled() || previewLanguage === MASTER_LANGUAGE) {
      return flat
    }
    return flat.map((entry) => applyTranslationOverlay(entry, previewLanguage))
  }, [draftQuestionnaire?.entries, previewLanguage])

  const { questionnaireState, dispatch } = useContext(QuestionnaireContext)

  if (!questionnaireState.isPreviewPanelOpened) {
    return null
  }

  return (
    <PreviewPanelControl
      entries={flatDraftQuestionnaireEntries}
      onClose={() => dispatch(closePreviewPanel())}
      onRefresh={() => {
        refetchQuestionnaire()
      }}
      previewLanguage={previewLanguage}
      onPreviewLanguageChange={setPreviewLanguage}
      availableLanguages={availableLanguages}
    />
  )
}

export default memo(PreviewPanel)
